export const plutopseudoBankPlaywrightScript = `import { createSession } from '@plutoxyz/automation';
import { chromium } from 'playwright-core';

const session = await createSession();

const input = await session.prompt({
  title: 'Login to Pseudo Bank',
  description: 'Enter your username and password to login to Pseudo Bank',
  prompts: [
    { label: 'Username', type: 'text', attributes: {} },
    { label: 'Password', type: 'password', attributes: {} },
  ],
});

const browser = await chromium.connectOverCDP(await session.cdp());
const context = browser.contexts()[0];
const page = context.pages()[0];

await page.goto('https://pseudo-bank.pluto.dev');
await page.getByRole('textbox', { name: 'Username' }).fill(input[0]);
await page.getByRole('textbox', { name: 'Password' }).fill(input[1]);
await page.getByRole('button', { name: 'Login' }).click();
await page.waitForSelector('text=Your Accounts', { timeout: 5000 });

const balanceLocator = page.locator('#balance-2');
await balanceLocator.waitFor({ state: 'visible', timeout: 5000 });
const balanceText = (await balanceLocator.textContent()) || '';
const balance = parseFloat(balanceText.replace(/[$,]/g, ''));

await session.prove('bank_balance', balance);

console.log('🎉 Session complete');

await page.close();
await browser.close();
await session.close();
  `;

export const venmoTransactionsPlaywrightScript = `
import { createSession } from "@plutoxyz/automation";
import { chromium } from 'playwright-core'

const session = await createSession()

const input = await session.prompt({
  title: 'Login to Venmo',
  description: 'Enter your Venmo credentials to prove your transactions',
  prompts: [
    {
      label: 'Email',
      type: 'text',
      attributes: {
        min_length: 3,
        max_length: 3,
        placeholder: 'Email or Phone Number'
      }
    },
    {
      label: 'Password',
      type: 'password',
      attributes: {
        placeholder: 'Password'
      }
    }
  ]
})

const browser = await chromium.connectOverCDP(await session.cdp());
const context = browser.contexts()[0];
const page = context.pages()[0];

await page.goto('https://id.venmo.com/signin')

await page.getByRole('textbox', { name: 'email' }).fill(input[0])
await page.getByRole('button', { name: 'Next' }).click()

await page.getByRole('textbox', { name: 'Password', timeout: 10000 }).fill(input[1])
await page.getByRole('button', { name: 'Log in' }).click()

await page.waitForSelector('h1', { timeout: 10000 })
const security = await page
  .getByRole('heading', { name: 'Confirm your identity' })
  .isVisible()
  .catch(() => false)

if (security) {
  await page.waitForTimeout(2000)
  await page.getByRole('button', { name: 'Send code' }).click()

  const otp = await session.prompt({
    title: 'Enter one time code',
    description: 'Enter the one time code sent to your phone',
    prompts: [
      {
        label: 'Code',
        type: 'number_code',
        attributes: {
          length: 6
        }
      }
    ]
  })

  await page.fill('#confirm-input', otp[0])
  await page.getByRole('button', { name: 'Confirm it' }).click()
}

// Wait for the feed page to load
await page.waitForSelector('#feed-tab-0, #feed-tab-1', { timeout: 10000 })

// Click the "mine" tab to show personal transactions
await page.click('#feed-tab-1')

// Wait for the personal transactions to load
await page.waitForSelector('[data-testid="me-feed-container"]', { timeout: 10000 })

// Let the page fully load
await page.waitForTimeout(5000)

// Scrape the transactions
const transactions = await page.evaluate(() => {
  const transactionData = []

  // Get all the article elements in the feed
  const articles = document.querySelectorAll('[data-testid="me-feed-container"] article')
  // Process each article
  articles.forEach((article) => {
    // Extract the headline using the id pattern which is more stable
    const headlineElement = article.querySelector('[id^="storyHeadlineId-"]')

    // Get transaction content using attribute selectors and structural position
    // Content is typically in a div that's a sibling to the div containing the timestamp
    let contentElement = null
    const timeElement = article.querySelector('div[aria-label*="ago"]')
    if (timeElement && timeElement.parentElement) {
      // Look for content in siblings of the timestamp's parent
      const parentContainer = timeElement.parentElement.parentElement
      if (parentContainer) {
        // Content is typically the next sibling after the div containing the time
        contentElement = parentContainer.nextElementSibling?.querySelector('div')
      }
    }

    // Get amount using structural position - it's the last direct child div of the article
    // This is the most stable approach as it doesn't rely on class names
    const allTopLevelDivs = article.querySelectorAll(':scope > div')
    const amountElement = allTopLevelDivs[allTopLevelDivs.length - 1]

    if (!headlineElement || !amountElement) return // Skip if missing crucial data

    // Get the text content
    const headline = headlineElement.textContent?.trim() || ''
    const content = contentElement?.textContent?.trim() || ''
    const amount = amountElement.textContent?.trim() || ''

    // Extract the timestamp from the time element
    const timestamp = timeElement ? timeElement.getAttribute('aria-label') || timeElement.textContent?.trim() : ''

    // Determine if this is a sent, received, or transfer transaction
    let type = 'unknown'
    if (amount.startsWith('+')) {
      type = 'received'
    } else if (amount.startsWith('-')) {
      type = 'sent'
    } else if (headline.includes('Transfer')) {
      type = 'transfer'
    }

    // Format the transaction description nicely
    let description = headline
    if (headline.includes('You paid')) {
      description = headline.replace('You paid', 'You sent money to')
    } else if (headline.includes('paid you')) {
      description = headline.replace('paid you', 'sent money to you')
    }

    // Remove positive/negative signs from amount
    const cleanAmount = amount.replace(/^[+-]\s*/, '')

    // Create a nice formatted summary with amount
    const fullSummary = headline + ' - ' + cleanAmount + (content ? ' (' + content + ')' : '')
    const summary = headline + ' - ' + cleanAmount
    transactionData.push({
      summary,
      type,
      amount,
      headline,
      content,
      timestamp
    })
  })

  return transactionData
})

// Get only recent transactions (up to 5)
const recentTransactions = transactions.slice(0, 5)

if (recentTransactions.length === 0) {
  await session.prove('venmo_transactions', {
    transactions: [],
    multiple_transactions: []
  })
} else {
  // Format transactions for display
  const formattedTransactions = recentTransactions.map((tx) => tx.summary)

  // Ask user to select transactions
  const selectResult = await session.prompt({
    title: 'Verify Venmo Transactions',
    description: 'Select transaction(s) you want to verify',
    prompts: [
      {
        label: 'Select Transactions',
        type: 'checkbox',
        required: false,
        attributes: {
          options: formattedTransactions,
          multiple: false, // Multi-transaction selection
          min: 1,
          max: 5
        }
      }
    ]
  })

  // Parse the JSON strings back to arrays
  const multipleSelection = selectResult[0] ? JSON.parse(selectResult[0]) : []
  const selectedMultipleTransactions = recentTransactions.filter((tx) => multipleSelection.includes(tx.summary))

  // Prove the selections
  await session.prove('venmo_transactions', selectedMultipleTransactions)
}

console.log('🎉 Session complete')

await page.close()
await browser.close()
await session.close()
`;

export const redditUsernamePlaywrightScript = `import { createSession } from '@plutoxyz/automation';
import { chromium } from 'patchright';

const AUTH_TYPES = {
  USERNAME: 'Email or Username',
  GOOGLE: 'Google',
};
const LOGIN_URL = 'https://www.reddit.com/login/';

/**
 * Detect if we run into a 2FA page and handle it
 * return Promise<void>
 */
const handleSecurityDetection = async () => {
  const security = !!(
    await page
      .getByRole('textbox', { name: 'Verification code' })
      .elementHandles()
  ).length;

  if (security) {
    console.log(\`Reddit 2FA detected, prompting for security code\`);

    const otp = await session.prompt({
      title: 'Enter one time code',
      description: 'Enter the one time code sent to your phone',
      prompts: [
        {
          label: 'Code',
          type: 'text',
          attributes: {
            length: 6,
          },
        },
      ],
    });

    console.log(\`Filling security code\`);
    await page.getByRole('textbox', { name: 'Verification code' }).fill(otp[0]);
    console.log(\`Security code filled\`);
    await page.getByRole('button', { name: 'Check code' }).click();
    await page.waitForLoadState('domcontentloaded');
  } else {
    console.log(\`No security code detected, continuing\`);
  }
};

const promptAuthType = async () =>
  await session.prompt({
    title: 'Log in',
    description: 'How do you want to log in',
    prompts: [
      {
        label: 'Login Type',
        type: 'checkbox',
        attributes: {
          multiple: false,
          options: Object.values(AUTH_TYPES),
        },
      },
    ],
  });

/**
 * Initialize session and initial prompt
 */
const session = await createSession();
console.log(\`Prompting for auth type\`);
const [authType] = await promptAuthType();
console.log(\`User chose \${authType[0]}\`);

/**
 * Browser Setup
 */
const browser = await chromium.connectOverCDP(await session.cdp());
const context = browser.contexts()[0];
const page = context.pages()[0];
console.log(\`Browser opened\`);

/**
 * Prompt the user for their username/email and Password
 * @return Promise<array> [username, password]
 */
const authUsernamePass = async (usernameError, passwordError) => {
  console.log(\`Prompting user for username/password\`);
  const [username, password] = await session.prompt({
    title: 'Login to Reddit',
    description: 'Enter your Reddit credentials to prove your data',
    prompts: [
      {
        label: 'Email or username',
        type: 'text',
        attributes: {
          min_length: 3,
          max_length: 3,
          placeholder: 'Email or username',
        },
        error: usernameError,
      },
      {
        label: 'Password',
        type: 'password',
        attributes: {
          placeholder: 'Password',
        },
        error: passwordError,
      },
    ],
  });

  console.log(\`Filling username\`);
  await page.getByRole('textbox', { name: 'Email or username' }).fill(username);
  console.log(\`Username filled\`);
  console.log(\`Filling Password\`);
  await page.getByRole('textbox', { name: 'Password' }).fill(password);
  console.log(\`Password filled\`);
  const a = page.getByRole('textbox', { name: 'Verification code' });
  await page.getByRole('button', { name: 'Log In' }).click();
  await page.waitForTimeout(2000);

  // Purposely use isVisible here since they keep this input in the
  // DOM regardless if there is 2FA enabled or not.  We can't rely on the
  // element purely existing as existance of 2FA being enabled.
  const has2FA = await page
    .getByRole('textbox', { name: 'Verification code' })
    .isVisible();

  if (has2FA) {
    console.log(\`Detected 2-Step Verification\`);
    const [code] = await session.prompt({
      title: 'Enter one time code',
      description: 'Enter the one time code from your authenticator app',
      prompts: [
        {
          label: 'Code',
          type: 'text',
          attributes: {
            length: 6,
          },
        },
      ],
    });

    await page.getByRole('textbox', { name: 'Verification code' }).fill(code);
    await page.getByRole('button', { name: 'Check code' }).click();
  } else {
    console.log(\`No 2-Step Verification detected, continuing\`);
  }
};

const authGoogle = async (usernameError, passwordError) => {
  await page.getByRole('button', { name: 'Continue with Google' }).click();
  console.log(\`Opening popup\`);
  const googlePage = await page.waitForEvent('popup');
  console.log(\`Popup opened\`);

  console.log(\`Disabling Passkeys\`);
  // 1. Create a DevTools session tied to this page
  const client = await context.newCDPSession(googlePage);

  // 2. Enable the WebAuthn domain
  await client.send('WebAuthn.enable');

  // 3. Add a virtual CTAP2 authenticator that auto-confirms everything
  const { authenticatorId } = await client.send(
    'WebAuthn.addVirtualAuthenticator',
    {
      options: {
        protocol: 'ctap2',
        transport: 'internal',
      },
    }
  );
  console.log(\`Passkeys disabled\`);

  console.log(\`Prompting user for credentials\`);
  const [username, password] = await session.prompt({
    title: 'Google Email or phone',
    description: 'Enter your Google credentials to login',
    prompts: [
      {
        label: 'Email or phone',
        type: 'text',
        attributes: {
          min_length: 3,
          max_length: 3,
          placeholder: 'Email or phone',
        },
        error: usernameError,
      },
      {
        label: 'Password',
        type: 'password',
        attributes: {
          placeholder: 'Password',
        },
        error: passwordError,
      },
    ],
  });

  console.log(\`Filling Email\`);
  await googlePage.waitForLoadState('domcontentloaded');
  await googlePage
    .getByRole('textbox', { name: 'Email or phone' })
    .fill(username);
  console.log(\`Email filled\`);
  await googlePage.getByText('Next').click();
  await googlePage.waitForNavigation();
  await googlePage.waitForTimeout(1000);
  await googlePage.getByRole('button', { name: 'Try another way' }).click();
  console.log(\`Filling password\`);
  await googlePage.getByText('Enter your password').click();
  await googlePage.waitForNavigation();
  await googlePage
    .getByRole('textbox', { name: 'Enter your password' })
    .fill(password);
  console.log(\`Password filled\`);
  await googlePage.getByText('Next').click();
  await googlePage.waitForNavigation();
  await googlePage.waitForLoadState('networkidle');
  await googlePage.waitForTimeout(3000);
  const has2FA = !!(
    await googlePage.getByText('2-Step Verification').elementHandles()
  ).length;

  if (has2FA) {
    console.log(\`Detected 2-Step Verification\`);

    const hasOptionalScreen = googlePage.getByText(
      'fingerprint, face, or screen lock'
    );
    if (!!(await hasOptionalScreen.elementHandles()).length) {
      console.log(\`Detected extra 2fa screen, continuing\`);
      await googlePage.getByRole('button', { name: 'Try another way' }).click();
      await googlePage.waitForTimeout(1000);
    }

    const tabletOption = {
      label: 'Tap Yes on your phone or tablet',
      locator: googlePage.getByRole('link', {
        name: ' on your phone or tablet',
      }),
      followup: async (_page, _code) => {},
      prompt: async () => {
        return {
          title: 'Confirm Access',
          description: 'Confirm you allowed access in your Google App',
          prompts: [
            {
              label: 'Confirm to continue',
              type: 'checkbox',
              attributes: {
                required: true,
                options: ['Confirm'],
              },
            },
          ],
        };
      },
    };
    const authenticator = {
      label: 'Get a verification code from the Google Authenticator app',
      locator: googlePage.getByRole('link', {
        name: 'Get a verification code from',
      }),
      followup: async (_page, _code) => {
        await _page
          .getByRole('textbox', {
            name: 'Enter code',
          })
          .fill(_code);
        await _page.getByText('Next').click();
      },
      prompt: () => ({
        title: 'Enter one time code',
        description: 'Enter the one time code sent to your phone',
        prompts: [
          {
            label: 'Code',
            type: 'text',
            attributes: {
              length: 6,
            },
          },
        ],
      }),
    };
    const smsCode = {
      label: 'Get a verification code sent to your phone',
      locator: googlePage.getByRole('link', {
        name: 'Get a verification code at',
      }),
      followup: async (_page, _code) => {
        await _page
          .getByRole('textbox', {
            name: 'Enter the code',
          })
          .fill(_code);
        await _page.getByText('Next').click();
      },
      prompt: () => ({
        title: 'Enter one time code',
        description: 'Enter the one time code sent to your phone',
        prompts: [
          {
            label: 'Code',
            type: 'text',
            attributes: {
              length: 6,
            },
          },
        ],
      }),
    };

    const allOptions = [tabletOption, authenticator, smsCode];
    const checkPromises = allOptions.map(async (option) => {
      try {
        if (!(await option.locator.elementHandles()).length) {
          console.warn(\`Option "\${option.label}" locator not visible.\`);
          return null;
        }

        const isEnabled = !(await option.locator.isDisabled());
        if (!isEnabled) {
          console.warn(
            \`Found disabled option: \${await option.locator.innerText()}\`
          );
        } else {
          console.log(
            \`Found enabled option \${await option.locator.innerText()}\`
          );
        }

        return isEnabled ? option : null;
      } catch (error) {
        // Handle cases where the locator might not resolve or other errors occur
        console.error(\`Error checking option "\${option.label}":\`, error);
        return null; // Treat errors as making the option unavailable
      }
    });

    const checkedOptions = await Promise.all(checkPromises);
    const enabledOptions = checkedOptions.filter((o) => !!o);
    if (enabledOptions.length > 0) {
      if (enabledOptions.length > 1) {
        console.log(
          \`Prompting user with enabled 2-Factor options.\${(await Promise.all(enabledOptions.map(async (e, i) => \`\${i + 1}) \${await e.locator.innerText()}\`))).join('\\n')}\`
        );

        const [choice] = await session.prompt({
          title: 'Two Factor Authentication',
          description: "Choose how you'd like to 2FA",
          prompts: [
            {
              label: 'Two Factor Method',
              type: 'checkbox',
              attributes: {
                multiple: false,
                options: enabledOptions.map((o) => o.label),
              },
            },
          ],
        });

        console.log(\`Selecting 2FA method\`);
        const match = enabledOptions.findIndex((i) => i.label === choice[0]);
        await enabledOptions[match].locator.click();
        console.log(\`Prompting user for 2FA\`);
        const [code] = await session.prompt(
          await enabledOptions[match].prompt()
        );

        if (enabledOptions[match].followup) {
          console.log(\`Entering 2FA Code\`);
          await enabledOptions[match].followup(googlePage, code);
          console.log(\`2FA code entered\`);
        }
      } else {
        console.log(
          \`Found a single enabled option: \${await enabledOptions[0].locator.innerText()}\`
        );

        await enabledOptions[0].locator.click();
        console.log(\`Prompting user for 2FA\`);
        const [code] = await session.prompt(await enabledOptions[0].prompt());
        if (enabledOptions[0].followup) {
          console.log(\`Entering 2FA Code\`);
          await enabledOptions[0].followup(googlePage, code);
          console.log(\`2FA code entered\`);
        }
      }
    } else {
      console.log(\`No enabled options found.\`);
      // Handle the case where no options are available
    }
  }
};

console.log(\`Navigating to \${LOGIN_URL}\`);
/**
 * Navigate to Login and enter credentials
 */
await page.goto(LOGIN_URL, {
  waitUntil: 'domcontentloaded',
});
console.log(\`\${LOGIN_URL} loaded\`);

await handleSecurityDetection();

switch (authType[0]) {
  case AUTH_TYPES.GOOGLE: {
    console.log(\`Authing Google\`);
    await authGoogle(undefined, undefined);
    await page.waitForLoadState('domcontentloaded');
    await handleSecurityDetection();
    break;
  }
  case AUTH_TYPES.USERNAME:
  default: {
    await authUsernamePass(undefined, undefined);
    break;
  }
}

/**
 * Navigate to User Profile
 */
console.log(\`User Logged In\`);
await page.waitForLoadState('domcontentloaded');
console.log(\`Navigating to User Profile\`);
await page.getByRole('button', { name: /expand user menu$/i }).click();
await page.getByText('View Profile').click();

/**
 * Scrape Username
 */
await page.waitForLoadState('domcontentloaded');
console.log(\`User Profile Loaded\`);
console.log(\`Scraping Username\`);
const handle = await page
  .getByLabel('Profile information')
  .getByRole('heading')
  .first()
  .innerText();
console.log(\`Username Scraped\`);

const [postKarma, commentKarma] = await page
  .getByTestId('karma-number')
  .allInnerTexts();

/**
 * Generate proof and cleanup
 */
console.log(\`Generating proof of username \${handle}\`);
await session.prove('reddit-data', [
  { redditUsername: handle },
  {
    totalKarma: (
      Number.parseInt(postKarma.replace(',', ''), 10) +
      Number.parseInt(commentKarma.replace(',', ''), 10)
    ).toLocaleString('en-US'),
  },
  { postKarma },
  { commentKarma },
]);
console.log(\`Proof generated\`);

console.log(\`Performing cleanup\`);
await page.close();
await browser.close();
await session.close();`;
